import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import ColumnFilter from "@/components/ColumnFilter";
import { useColFilter } from "@/lib/tableFilter";
import { Wallet, Plus, Upload, Download, FileText, Settings, Pencil, Trash2, X, Check, Link2, RotateCcw } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const GELIR_PALETTE = ["#2F6E4E", "#30B0C7", "#0A84FF", "#5E5CE6", "#00C7BE", "#32ADE6"];
const GIDER_PALETTE = ["#A63A3A", "#B8933F", "#FF2D55", "#7C5E9B", "#FF6482", "#A2845E", "#A79A86"];

const empty = { tarih: "", aciklama: "", kategori: "", giris: "", cikis: "" };

const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const ayLabel = (m) => { const [y, mo] = (m || "").split("-"); return mo ? `${AY_ADLARI[parseInt(mo, 10) - 1]} ${y}` : m; };

export default function IlDisiKasa() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [ay, setAy] = useState("");
  const [aylar, setAylar] = useState([]);
  const [form, setForm] = useState(null); // {id?, ...fields}
  const [showKat, setShowKat] = useState(false);
  const [showEslestir, setShowEslestir] = useState(false);
  const [eslestirKasaId, setEslestirKasaId] = useState(null);
  const [editAcilis, setEditAcilis] = useState(false);
  const [acilisVal, setAcilisVal] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const [s, h, k] = await Promise.all([
      api.get("/kasa/summary", { params }),
      api.get("/kasa/hareketler", { params }),
      api.get("/kasa/kategoriler"),
    ]);
    setSummary(s.data);
    setRows(h.data || []);
    setKategoriler(k.data || []);
    setAcilisVal(String(s.data.acilis ?? 0));
  }, [start, end]);

  useEffect(() => { load().catch(() => toast.error("Kasa verisi yüklenemedi")); }, [load]);

  useEffect(() => { api.get("/kasa/aylar").then(({ data }) => setAylar(data.aylar || [])).catch(() => {}); }, []);

  const pickAy = (m) => {
    setAy(m);
    if (m) { setStart(`${m}-01`); setEnd(`${m}-31`); }
    else { setStart(""); setEnd(""); }
  };

  const saveHareket = async () => {
    const body = {
      tarih: form.tarih || null,
      aciklama: form.aciklama || "",
      kategori: form.kategori || "",
      giris: parseFloat(form.giris) || 0,
      cikis: parseFloat(form.cikis) || 0,
    };
    try {
      if (form.id) {
        await api.put(`/kasa/hareketler/${form.id}`, body);
        toast.success("Kaydedildi");
        setForm(null);
        load();
      } else {
        const { data } = await api.post("/kasa/hareketler", body);
        toast.success("Kaydedildi");
        setForm(null);
        load();
        // Yeni 'İcra Tahsilatı' hareketi eklendiyse hareket bazlı eşleştirme önizlemesini aç
        if (body.kategori === "İcra Tahsilatı" && body.giris > 0 && data?.id) {
          setEslestirKasaId(data.id);
          setShowEslestir(true);
        }
      }
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const delHareket = async (id) => {
    if (!window.confirm("Bu hareket silinsin mi?")) return;
    try { await api.delete(`/kasa/hareketler/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const undoEslesme = async (id) => {
    if (!window.confirm("Bu tahsilatın İcra dosyasıyla eşleşmesi geri alınacak (dosyadan ilgili tahsilat silinecek). Onaylıyor musunuz?")) return;
    try { await api.post(`/kasa/eslestir/geri-al/${id}`); toast.success("Eşleşme geri alındı"); load(); }
    catch (e) { toast.error("Geri alınamadı"); }
  };

  const saveAcilis = async () => {
    try { await api.put("/kasa/acilis", { acilis: parseFloat(acilisVal) || 0 }); setEditAcilis(false); load(); toast.success("Açılış bakiyesi güncellendi"); }
    catch (e) { toast.error("Güncellenemedi"); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Bu Excel içe aktarılacak. Mevcut hareketler KORUNUR; yalnızca dosyadaki tarih aralığındaki kayıtlar güncellenir (eski aylar eklenebilir). Devam edilsin mi?")) { e.target.value = ""; return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kasa/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} hareket içe aktarıldı`);
      load();
    } catch (err) { toast.error("İçe aktarma başarısız"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const ekstreRef = useRef(null);
  const [ekstreRows, setEkstreRows] = useState(null);
  const [ekstreOzet, setEkstreOzet] = useState(null);
  const [ekstreBusy, setEkstreBusy] = useState(false);

  const doEkstre = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEkstreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kasa/ekstre/onizleme", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEkstreRows((data.rows || []).map((r) => ({ ...r, _ekle: !r.mevcut })));
      setEkstreOzet(data.ozet);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ekstre okunamadı");
    } finally {
      setEkstreBusy(false);
      if (ekstreRef.current) ekstreRef.current.value = "";
    }
  };

  const ekstreUygula = async () => {
    const secili = (ekstreRows || []).filter((r) => r._ekle);
    if (secili.length === 0) { toast.error("Eklenecek satır seçilmedi"); return; }
    const kategorisiz = secili.filter((r) => !r.kategori);
    if (kategorisiz.length > 0) { toast.error(`${kategorisiz.length} satırın kategorisi boş. Düzeltin veya listeden çıkarın.`); return; }
    setEkstreBusy(true);
    try {
      const { data } = await api.post("/kasa/ekstre/uygula", { rows: secili.map((r) => ({ tarih: r.tarih, aciklama: r.aciklama, kategori: r.kategori, giris: r.giris, cikis: r.cikis, islem_no: r.islem_no })) });
      toast.success(`${data.eklendi} hareket kasaya eklendi${data.atlandi ? `, ${data.atlandi} mükerrer atlandı` : ""}`);
      const tahsilatEklendi = data.eklendi > 0 && secili.some((r) => r.kategori === "İcra Tahsilatı" && r.giris > 0);
      setEkstreRows(null); setEkstreOzet(null);
      await load();
      // Ekstreden 'İcra Tahsilatı' eklendiyse, İcra eşleştirme önizlemesini otomatik aç (dosya no + isim benzerliği ile)
      if (tahsilatEklendi) {
        setEslestirKasaId(null);
        setShowEslestir(true);
        toast.info("İcra Tahsilatı satırları için dosya eşleştirme önizlemesi açıldı — dosyaları onaylayın.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ekleme başarısız");
    } finally { setEkstreBusy(false); }
  };

  const download = async (type) => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/kasa/export/${type}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "il_disi_kasa.xlsx" : "il_disi_kasa.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Kasa export başarısız:", e); toast.error("Dışa aktarma başarısız"); }
  };

  const addKat = async (ad, tur) => {
    if (!ad.trim()) return;
    try { await api.post("/kasa/kategoriler", { ad: ad.trim(), tur }); load(); }
    catch (e) { toast.error("Kategori eklenemedi"); }
  };
  const delKat = async (id) => {
    try { await api.delete(`/kasa/kategoriler/${id}`); load(); }
    catch (e) { toast.error("Kategori silinemedi"); }
  };

  const cards = summary ? [
    { key: "acilis", label: "Açılış Bakiyesi", val: summary.acilis, tone: "#26231E", editable: true },
    { key: "giris", label: "Toplam Giriş", val: summary.toplam_giris, tone: "#2F6E4E" },
    { key: "cikis", label: "Toplam Çıkış (Gider)", val: summary.toplam_cikis, tone: "#A63A3A" },
    { key: "virman", label: "Virman (Müvekkil Öd.)", val: summary.toplam_virman, tone: "#B8933F" },
    { key: "bakiye", label: "Güncel Kasa Bakiyesi", val: summary.guncel_bakiye, tone: "#B8933F" },
  ] : [];

  const KCOLS = [
    { key: "tarih", label: "Tarih", type: "date" },
    { key: "aciklama", label: "Açıklama", type: "text" },
    { key: "kategori", label: "Kategori", type: "text" },
    { key: "giris", label: "Giriş", type: "money" },
    { key: "cikis", label: "Çıkış", type: "money" },
  ];
  // En yeni eklenen hareket en üstte: created_at (yoksa tarih) azalan sırayla
  const sortedRows = useMemo(() => {
    const ts = (r) => {
      const c = r.created_at ? Date.parse(r.created_at) : NaN;
      if (!isNaN(c)) return c;
      const t = r.tarih ? Date.parse(String(r.tarih).slice(0, 10)) : NaN;
      return isNaN(t) ? 0 : t;
    };
    return [...rows].sort((a, b) => ts(b) - ts(a));
  }, [rows]);
  const { colSel, setColSel, sort, setSort, view, distinct } = useColFilter(KCOLS, sortedRows);
  const kfGiris = view.reduce((s, r) => s + (r.giris || 0), 0);
  const kfCikis = view.reduce((s, r) => s + (r.cikis || 0), 0);

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
        <div className="mr-auto text-sm text-[#8C8377]">Kasa defteri — giriş/çıkış hareketleri, kategori bazlı özet.</div>
          <button onClick={() => setForm({ ...empty })} data-testid="kasa-add-btn" className="flex items-center gap-1.5 px-3 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Plus className="w-4 h-4" /> Hareket Ekle</button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="kasa-import-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150 disabled:opacity-50"><Upload className="w-4 h-4 text-[#B8933F]" /> {importing ? "Aktarılıyor…" : "Excel'den İçe Aktar"}</button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={doImport} data-testid="kasa-import-input" />

          <button onClick={() => ekstreRef.current?.click()} disabled={ekstreBusy} data-testid="kasa-ekstre-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#B8933F] bg-[#B8933F]/10 rounded-sm text-sm font-medium hover:bg-[#B8933F]/20 transition-colors duration-150 disabled:opacity-50"><Upload className="w-4 h-4 text-[#B8933F]" /> {ekstreBusy ? "Okunuyor…" : "Ekstre Yükle (PDF)"}</button>
          <input ref={ekstreRef} type="file" accept=".pdf" className="hidden" onChange={doEkstre} data-testid="kasa-ekstre-input" />
          <button onClick={() => { setEslestirKasaId(null); setShowEslestir(true); }} data-testid="kasa-eslestir-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#B8933F] text-[#B8933F] bg-white rounded-sm text-sm font-medium hover:bg-[#B8933F]/5 transition-colors duration-150"><Link2 className="w-4 h-4" /> İcra Dosyalarıyla Eşleştir</button>
          <button onClick={() => setShowKat(true)} data-testid="kasa-kategori-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><Settings className="w-4 h-4" /> Kategoriler</button>
          <button onClick={() => download("excel")} data-testid="kasa-excel-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><Download className="w-4 h-4 text-[#2F6E4E]" /> Excel</button>
          <button onClick={() => download("pdf")} data-testid="kasa-pdf-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><FileText className="w-4 h-4 text-[#A63A3A]" /> PDF</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.key} className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: `3px solid ${c.tone}` }} data-testid={`kasa-card-${c.key}`}>
            <div className="kicker mb-1.5 flex items-center justify-between">
              {c.label}
              {c.editable && !editAcilis && <button onClick={() => setEditAcilis(true)} data-testid="kasa-acilis-edit" className="text-[#B8933F] hover:opacity-70"><Pencil className="w-3.5 h-3.5" /></button>}
            </div>
            {c.editable && editAcilis ? (
              <div className="flex items-center gap-1">
                <input type="number" value={acilisVal} onChange={(e) => setAcilisVal(e.target.value)} data-testid="kasa-acilis-input" className="w-28 px-2 py-1 border border-[#E7DECC] rounded-sm text-sm" />
                <button onClick={saveAcilis} data-testid="kasa-acilis-save" className="text-[#2F6E4E]"><Check className="w-4 h-4" /></button>
                <button onClick={() => { setEditAcilis(false); setAcilisVal(String(summary.acilis)); }} className="text-[#A63A3A]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="font-heading font-black text-xl tabular-nums" style={{ color: c.tone }}>{fmt(c.val)}</div>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div><label className="kicker block mb-1">AY</label>
          <select value={ay} onChange={(e) => pickAy(e.target.value)} data-testid="kasa-ay-select" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white min-w-[160px]">
            <option value="">Tüm Aylar</option>
            {aylar.map((m) => <option key={m} value={m}>{ayLabel(m)}</option>)}
          </select>
        </div>
        <div><label className="kicker block mb-1">BAŞLANGIÇ</label><input type="date" value={start} onChange={(e) => { setStart(e.target.value); setAy(""); }} data-testid="kasa-start" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
        <div><label className="kicker block mb-1">BİTİŞ</label><input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setAy(""); }} data-testid="kasa-end" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
        {(start || end || ay) && <button onClick={() => { setAy(""); setStart(""); setEnd(""); }} data-testid="kasa-clear" className="px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A]">Temizle</button>}
        <div className="ml-auto text-sm text-[#8C8377]">{rows.length} hareket · {aylar.length} ay</div>
      </div>

      {/* Hareket tablosu */}
      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden mb-8">
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm" data-testid="kasa-table">
            <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10">
              <tr>
                <th className="px-3 py-2.5 kicker text-left w-12">Sıra</th>
                {KCOLS.map((c) => (
                  <th key={c.key} className={`px-3 py-2.5 kicker ${c.type === "money" ? "text-right" : "text-left"}`}>
                    <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                      <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                      <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2.5 kicker text-right w-20">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]" data-testid="kasa-empty">Hareket yok. "Hareket Ekle" veya "Excel'den İçe Aktar" ile başlayın.</td></tr>
              ) : view.map((r, i) => (
                <tr key={r.id} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`kasa-row-${r.id}`}>
                  <td className="px-3 py-2 tabular-nums text-[#8C8377]">{i + 1}</td>
                  <td className="px-3 py-2 tabular-nums">{fmtDate(r.tarih)}</td>
                  <td className="px-3 py-2">{r.aciklama}</td>
                  <td className="px-3 py-2 text-[#8C8377]">{r.kategori}{r.eslesildi && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-sm bg-[#2F6E4E]/10 text-[#2F6E4E]" title={`İcra dosyasına işlendi: ${r.eslesme_musteri || ""}`}>eşlendi ✓</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{r.giris ? fmt(r.giris) : ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{r.cikis ? fmt(r.cikis) : ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.eslesildi && <button onClick={() => undoEslesme(r.id)} data-testid={`kasa-undo-${r.id}`} title="Eşleşmeyi geri al" className="text-[#B8933F] hover:opacity-70 mr-2"><RotateCcw className="w-4 h-4 inline" /></button>}
                    <button onClick={() => setForm({ id: r.id, tarih: (r.tarih || "").slice(0, 10), aciklama: r.aciklama || "", kategori: r.kategori || "", giris: r.giris || "", cikis: r.cikis || "" })} data-testid={`kasa-edit-${r.id}`} className="text-[#B8933F] hover:opacity-70 mr-2"><Pencil className="w-4 h-4 inline" /></button>
                    <button onClick={() => delHareket(r.id)} data-testid={`kasa-del-${r.id}`} className="text-[#A63A3A] hover:opacity-70"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            {view.length > 0 && (
              <tfoot className="sticky bottom-0">
                <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                  <td className="px-3 py-2.5" colSpan={4}>FİLTRELİ TOPLAM ({view.length})</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#2F6E4E]">{fmt(kfGiris)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#A63A3A]">{fmt(kfCikis)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Dağılım grafikleri */}
      {summary && (summary.gelirler.length > 0 || summary.giderler.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="GELİR DAĞILIMI (Kategori Bazlı Giriş)" testid="kasa-chart-gelir"
            data={summary.gelirler.map((x) => ({ name: x.kategori, value: x.giris })).filter((d) => d.value > 0)} palette={GELIR_PALETTE} />
          <ChartCard title="GİDER DAĞILIMI (Kategori Bazlı Çıkış)" testid="kasa-chart-gider"
            data={summary.giderler.map((x) => ({ name: x.kategori, value: x.cikis })).filter((d) => d.value > 0)} palette={GIDER_PALETTE} />
        </div>
      )}

      {/* Kategori bazlı özet */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SummaryTable title="GELİRLER (Tahsilat / Gelen)" color="#2F6E4E" rows={summary.gelirler} ara={summary.gelir_ara} />
          <SummaryTable title="GİDERLER (Ödeme / Giden)" color="#A63A3A" rows={summary.giderler} ara={summary.gider_ara} />
        </div>
      )}
      {summary && (
        <div className="mt-4 bg-[#B8933F] text-white rounded-sm p-4 flex flex-wrap items-center justify-between gap-4" data-testid="kasa-genel-toplam">
          <span className="font-heading font-bold text-lg">GENEL TOPLAM</span>
          <div className="flex flex-wrap gap-6 text-sm">
            <span>Giriş: <strong className="tabular-nums">{fmt(summary.genel.giris)}</strong></span>
            <span>Çıkış: <strong className="tabular-nums">{fmt(summary.genel.cikis)}</strong></span>
            <span>Net: <strong className="tabular-nums">{fmt(summary.genel.net)}</strong></span>
          </div>
        </div>
      )}

      {/* Hareket ekle/düzenle modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="kasa-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">{form.id ? "Hareketi Düzenle" : "Yeni Hareket"}</h3>
              <button onClick={() => setForm(null)} className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="kicker block mb-1">TARİH</label><input type="date" value={form.tarih} onChange={(e) => setForm({ ...form, tarih: e.target.value })} data-testid="kasa-form-tarih" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
              <div><label className="kicker block mb-1">AÇIKLAMA</label><input type="text" value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} data-testid="kasa-form-aciklama" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
              <div><label className="kicker block mb-1">KATEGORİ</label>
                <select value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} data-testid="kasa-form-kategori" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white">
                  <option value="">Seçiniz…</option>
                  {kategoriler.map((k) => <option key={k.id} value={k.ad}>{k.ad} ({k.tur})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="kicker block mb-1">GİRİŞ (₺)</label><input type="number" value={form.giris} onChange={(e) => setForm({ ...form, giris: e.target.value })} data-testid="kasa-form-giris" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
                <div><label className="kicker block mb-1">ÇIKIŞ (₺)</label><input type="number" value={form.cikis} onChange={(e) => setForm({ ...form, cikis: e.target.value })} data-testid="kasa-form-cikis" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setForm(null)} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">İptal</button>
              <button onClick={saveHareket} data-testid="kasa-form-save" className="px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Kategori yönetimi modal */}
      {showKat && <KategoriModal kategoriler={kategoriler} onAdd={addKat} onDel={delKat} onClose={() => setShowKat(false)} />}

      {/* İcra eşleştirme modal */}
      {showEslestir && <EslestirModal kasaId={eslestirKasaId} onClose={() => { setShowEslestir(false); setEslestirKasaId(null); }} onDone={load} />}

      {/* Ekstre önizleme modal */}
      {ekstreRows && <EkstreModal rows={ekstreRows} setRows={setEkstreRows} ozet={ekstreOzet} kategoriler={kategoriler} busy={ekstreBusy} onApply={ekstreUygula} onClose={() => { setEkstreRows(null); setEkstreOzet(null); }} />}
    </>
  );
}

function ChartCard({ title, data, palette, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid={testid}>
      <div className="kicker mb-2 flex items-center justify-between">
        <span>{title}</span>
        <span className="text-[#26231E] font-heading font-bold tabular-nums">{fmt(total)}</span>
      </div>
      {data.length === 0 ? (
        <div className="py-16 text-center text-[#8C8377] text-sm">Veri yok</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={95} innerRadius={50} paddingAngle={2}>
              {data.map((d) => <Cell key={d.name} fill={palette[data.indexOf(d) % palette.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [fmt(v), n]} />
            <Legend verticalAlign="bottom" height={48} iconType="circle" formatter={(val) => <span style={{ fontSize: 11, color: "#8C8377" }}>{val}</span>} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SummaryTable({ title, color, rows, ara }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden" data-testid={`kasa-summary-${color === "#2F6E4E" ? "gelir" : "gider"}`}>
      <div className="px-4 py-2.5 font-heading font-bold text-sm" style={{ backgroundColor: `${color}1a`, color }}>{title}</div>
      <table className="w-full text-sm">
        <thead className="border-b border-[#E7DECC] bg-[#F3ECDD]"><tr>
          <th className="px-3 py-2 kicker text-left">Kategori</th>
          <th className="px-3 py-2 kicker text-right">Giriş</th>
          <th className="px-3 py-2 kicker text-right">Çıkış</th>
          <th className="px-3 py-2 kicker text-right">Net</th>
        </tr></thead>
        <tbody>
          {(rows || []).length === 0 ? (
            <tr><td colSpan={4} className="px-3 py-6 text-center text-[#8C8377]">—</td></tr>
          ) : rows.map((r) => (
            <tr key={r.kategori} className="border-b border-[#EFE7D6]">
              <td className="px-3 py-2">{r.kategori}</td>
              <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{r.giris ? fmt(r.giris) : ""}</td>
              <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{r.cikis ? fmt(r.cikis) : ""}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.net)}</td>
            </tr>
          ))}
          <tr className="bg-[#F3ECDD] font-bold">
            <td className="px-3 py-2">ARA TOPLAM</td>
            <td className="px-3 py-2 text-right tabular-nums">{fmt(ara?.giris || 0)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{fmt(ara?.cikis || 0)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{fmt((ara?.giris || 0) - (ara?.cikis || 0))}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function KategoriModal({ kategoriler, onAdd, onDel, onClose }) {
  const [ad, setAd] = useState("");
  const [tur, setTur] = useState("gider");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="kasa-kategori-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Kategoriler</h3>
          <button onClick={onClose} className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-end gap-2 mb-4">
          <div className="flex-1"><label className="kicker block mb-1">YENİ KATEGORİ</label><input type="text" value={ad} onChange={(e) => setAd(e.target.value)} data-testid="kasa-kat-ad" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm" /></div>
          <select value={tur} onChange={(e) => setTur(e.target.value)} data-testid="kasa-kat-tur" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white"><option value="gelir">Gelir</option><option value="gider">Gider</option><option value="virman">Virman</option></select>
          <button onClick={() => { onAdd(ad, tur); setAd(""); }} data-testid="kasa-kat-add" className="px-3 py-2 bg-[#B8933F] text-white rounded-sm text-sm font-medium"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="max-h-64 overflow-auto border border-[#E7DECC] rounded-sm">
          {kategoriler.map((k) => (
            <div key={k.id} className="flex items-center justify-between px-3 py-2 border-b border-[#EFE7D6]" data-testid={`kasa-kat-item-${k.id}`}>
              <span>{k.ad} <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-sm ${k.tur === "gelir" ? "bg-[#2F6E4E]/10 text-[#2F6E4E]" : k.tur === "virman" ? "bg-[#B8933F]/10 text-[#B8933F]" : "bg-[#A63A3A]/10 text-[#A63A3A]"}`}>{k.tur}</span></span>
              <button onClick={() => onDel(k.id)} className="text-[#A63A3A] hover:opacity-70"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EslestirModal({ kasaId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const loadPreview = useCallback(() => {
    api.get("/kasa/eslestir/onizleme", { params: kasaId ? { kasa_id: kasaId } : {} }).then(({ data }) => {
      setData(data);
      const init = {};
      (data.rows || []).forEach((r) => {
        init[r.kasa_id] = { checked: r.candidates.length === 1, record_id: r.candidates.length === 1 ? r.candidates[0].id : "" };
      });
      setSel(init);
    }).catch(() => toast.error("Eşleştirme önizlemesi yüklenemedi"));
  }, [kasaId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const apply = async () => {
    const eslesmeler = Object.entries(sel).filter(([, v]) => v.checked && v.record_id).map(([kasa_id, v]) => ({ kasa_id, record_id: v.record_id }));
    if (eslesmeler.length === 0) { toast.error("İşlenecek seçim yok"); return; }
    if (!window.confirm(`${eslesmeler.length} tahsilat, seçilen İcra dosyalarına işlenecek. Onaylıyor musunuz?`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/kasa/eslestir/uygula", { eslesmeler }, { timeout: 120000 });
      toast.success(`${res.applied} tahsilat işlendi${res.skipped ? `, ${res.skipped} atlandı` : ""}${res.hata ? `, ${res.hata} hata` : ""}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem tamamlanamadı, ekran tazelendi — tekrar deneyebilirsiniz");
      loadPreview();  // zaten işlenenler listeden düşsün, mükerrer olmasın
    }
    finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  const records = data?.records || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()} data-testid="kasa-eslestir-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold text-lg">{kasaId ? "Bu Tahsilatı İcra Dosyasıyla Eşleştir" : "İcra Dosyalarıyla Eşleştir"}</h3>
          <button onClick={onClose} className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#8C8377] mb-4">{kasaId ? "Yeni eklenen 'İcra Tahsilatı' hareketi. İcra dosyasını (Dosya No) seçip işaretleyin; onaylanınca dosyaya \"dosya\" türü tahsilat olarak işlenir. Eşleştirmek istemiyorsanız kapatabilirsiniz — sonra 'İcra Dosyalarıyla Eşleştir' butonundan yapabilirsiniz." : "Tüm \"İcra Tahsilatı\" hareketleri. Her satırda İcra dosyasını seçip işaretleyin; onaylananlar ilgili dosyaya \"dosya\" türü tahsilat olarak işlenir."}</p>
        {!data ? (
          <div className="py-10 text-center text-[#8C8377]">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-[#8C8377]" data-testid="kasa-eslestir-empty">Eşleştirilecek (henüz eşleşmemiş) İcra Tahsilatı hareketi yok.</div>
        ) : (
          <div className="max-h-[440px] overflow-auto border border-[#E7DECC] rounded-sm">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
                <th className="px-2 py-2 kicker text-center w-8"></th>
                <th className="px-2 py-2 kicker text-left">Tarih</th>
                <th className="px-2 py-2 kicker text-right">Tutar</th>
                <th className="px-2 py-2 kicker text-left">Açıklama</th>
                <th className="px-2 py-2 kicker text-left">İcra Dosyası</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const s = sel[r.kasa_id] || { checked: false, record_id: "" };
                  const hint = r.candidates.length === 1 ? "tek eşleşme" : r.candidates.length > 1 ? `${r.candidates.length} aday` : "eşleşme yok";
                  return (
                    <tr key={r.kasa_id} className="border-b border-[#EFE7D6]" data-testid={`kasa-eslestir-row-${r.kasa_id}`}>
                      <td className="px-2 py-2 text-center"><input type="checkbox" checked={s.checked} onChange={(e) => setSel({ ...sel, [r.kasa_id]: { ...s, checked: e.target.checked } })} data-testid={`kasa-eslestir-chk-${r.kasa_id}`} /></td>
                      <td className="px-2 py-2 tabular-nums">{fmtDate(r.tarih)}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-[#2F6E4E]">{fmt(r.giris)}</td>
                      <td className="px-2 py-2"><div>{r.aciklama}</div><div className="text-xs text-[#8C8377]">{r.ad} · {hint}</div></td>
                      <td className="px-2 py-2">
                        <select value={s.record_id} onChange={(e) => setSel({ ...sel, [r.kasa_id]: { ...s, record_id: e.target.value, checked: e.target.value ? s.checked : false } })} data-testid={`kasa-eslestir-sel-${r.kasa_id}`} className="w-full px-2 py-1 border border-[#E7DECC] rounded-sm text-sm bg-white">
                          <option value="">— Seçiniz —</option>
                          {(r.candidates.length > 0 ? r.candidates : records).map((c) => <option key={c.id} value={c.id}>{c.musteri} {c.dosya_no ? `(${c.dosya_no})` : ""}{c.skor && c.skor < 1 ? ` ~%${Math.round(c.skor * 100)}` : ""}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">Kapat</button>
          <button onClick={apply} disabled={busy || rows.length === 0} data-testid="kasa-eslestir-apply" className="px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">{busy ? "İşleniyor…" : "Seçilenleri İşle"}</button>
        </div>
      </div>
    </div>
  );
}

function EkstreModal({ rows, setRows, ozet, kategoriler, busy, onApply, onClose }) {
  const toggle = (i) => setRows(rows.map((r, idx) => (idx === i ? { ...r, _ekle: !r._ekle } : r)));
  const setKat = (i, ad) => setRows(rows.map((r, idx) => (idx === i ? { ...r, kategori: ad, bulunamadi: !ad } : r)));
  const setAll = (v) => setRows(rows.map((r) => ({ ...r, _ekle: v })));
  const seciliAdet = rows.filter((r) => r._ekle).length;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ekstre-modal">
      <div className="bg-white rounded-sm w-full max-w-6xl max-h-[90vh] flex flex-col border border-[#E7DECC]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
          <h3 className="font-heading font-bold text-lg">Ekstre Önizleme — Kasaya Ekle</h3>
          <button onClick={onClose} data-testid="ekstre-close" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        {ozet && (
          <div className="px-5 py-2.5 bg-[#F3ECDD] text-sm flex flex-wrap gap-x-5 gap-y-1 border-b border-[#E7DECC]">
            <span>Toplam: <b>{ozet.toplam}</b></span>
            <span className="text-[#2F6E4E]">Giriş: <b>{ozet.giris_adet}</b> ({fmt(ozet.toplam_giris)})</span>
            <span className="text-[#A63A3A]">Çıkış: <b>{ozet.cikis_adet}</b> ({fmt(ozet.toplam_cikis)})</span>
            {ozet.bulunamadi_adet > 0 && <span className="text-[#A63A3A]">Kategorisiz: <b>{ozet.bulunamadi_adet}</b></span>}
            {ozet.mevcut_adet > 0 && <span className="text-[#8C8377]">Zaten var: <b>{ozet.mevcut_adet}</b></span>}
          </div>
        )}
        <div className="px-5 py-1.5 border-b border-[#E7DECC] flex gap-3 text-xs">
          <button onClick={() => setAll(true)} className="text-[#B8933F] hover:underline" data-testid="ekstre-select-all">Tümünü seç</button>
          <button onClick={() => setAll(false)} className="text-[#8C8377] hover:underline" data-testid="ekstre-deselect-all">Tümünü kaldır</button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b border-[#E7DECC] z-10"><tr className="text-left text-[#8C8377]">
              <th className="px-3 py-2 w-10">Ekle</th><th className="px-3 py-2">Tarih</th><th className="px-3 py-2">Açıklama</th>
              <th className="px-3 py-2 text-right">Giriş</th><th className="px-3 py-2 text-right">Çıkış</th>
              <th className="px-3 py-2">Kategori</th><th className="px-3 py-2">Durum</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-b border-[#E7DECC] ${r.mevcut ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`ekstre-row-${i}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={!!r._ekle} onChange={() => toggle(i)} data-testid={`ekstre-chk-${i}`} /></td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDate ? fmtDate(r.tarih) : r.tarih}</td>
                  <td className="px-3 py-2 max-w-md truncate" title={r.aciklama_tam || r.aciklama}>{r.aciklama}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{r.giris > 0 ? fmt(r.giris) : ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{r.cikis > 0 ? fmt(r.cikis) : ""}</td>
                  <td className="px-3 py-2">
                    <select value={r.kategori} onChange={(e) => setKat(i, e.target.value)} data-testid={`ekstre-kat-${i}`} className={`px-2 py-1 border rounded-sm text-sm bg-white ${!r.kategori ? "border-[#A63A3A]" : "border-[#E7DECC]"}`}>
                      <option value="">— seçin —</option>
                      {kategoriler.map((k) => <option key={k.id} value={k.ad}>{k.ad}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {r.mevcut && <span className="px-1.5 py-0.5 rounded-sm bg-[#8C8377]/15 text-[#8C8377]">zaten var</span>}
                    {!r.mevcut && !r.kategori && <span className="px-1.5 py-0.5 rounded-sm bg-[#A63A3A]/15 text-[#A63A3A]">kategori bulunamadı</span>}
                    {!r.mevcut && r.kategori && <span className="px-1.5 py-0.5 rounded-sm bg-[#2F6E4E]/15 text-[#2F6E4E]">hazır</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#E7DECC]">
          <span className="text-sm text-[#8C8377]"><b>{seciliAdet}</b> satır seçili</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm" data-testid="ekstre-cancel">İptal</button>
            <button onClick={onApply} disabled={busy || seciliAdet === 0} data-testid="ekstre-uygula-btn" className="px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">{busy ? "Ekleniyor…" : `Kasaya Ekle (${seciliAdet})`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
